import React, { ReactNode, useEffect, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import { Router, useRouter } from "next/router";
import { NavigationType, CategoryType, AppTheme } from '../helper/enums'

import useLocalStorage from "../helper/use_local_storage";

import { AppContext } from '../provider/AppContextProvider';

import { getCategoryInformation } from '../helper/useCategoryHelper'
import Loading from "./ui/Loading";
import useHeaderHeight from "../lib/useHeaderHeight";
import { closePianoOffer } from "../helper/piano";
import AudioPlayer from "./audioPlayer";
import { useQueryInAppPurchases } from "../helper/useQueryInAppPurchases";
import { sanitizeUrl, useLoggedRouter } from "../helper/useLoggedRouter";
import { logBugsnagError } from "../lib/bugsnag";
import { update } from "../helper/sso";
import { AppInfo, parsePlatform } from "../helper/interfaces";

declare global {
  interface Window {
    navigationStart(data: { url: String, backEnabled: boolean, type: String, isApp: boolean }): Function;
    navigationComplete(data: { url: String, backEnabled: boolean, type: String, isApp: boolean }): Function;
    getNavigationData: () => { url: String, backEnabled: boolean, type: String, isApp: boolean };
    externalNavigate(data: { url: String }): Function;
    navigateBack: Function;
    navigateTo: (url: String) => void;
    navigateToWithoutNotification: (url: String) => void;
    navigateMainViewTo: (url: String) => void; // is called from the webapp
    reload: (inBackroundForSeconds?: number) => Promise<string>; // is called from the webapp
    reloadSync: (inBackroundForSeconds?: number) => string; // is called from the webapp
    crash: Function;
    setLocalStorageItemWithNotify(key: string, value: string): void;
    queryLocalStorageItem(key: string): void; // triggers getLocalStorageItemCallback
    localStorageItemCallback(key: string, value: string): void;
    setUserState(signedIn: boolean, plusUser: boolean): void;
    ready: () => void; // is called from the webapp

     //pushes
     pushesIsSubscribed: () => void; // is called from the webapp and expects pushesSubscriptionsCallback to be called from app
     pushesSubscriptionsCallback: (data: [{ id: string, text: string, isSubscribed: boolean}]) => void; // is called from the app
     pushesSetSubscriptions: (data: [{ id: string, text: string, isSubscribed: boolean}]) => void; // is called from the webapp and expects pushesSubscriptionsCallback to be called from app
     pushesGetChannelId: () => string;
     setPushSubscriptionId: (version: string) => void //deprecated
     queryPushSubscriptionId: () => void //deprecated

     logError: (message: string) => void

     setAppVersion: (version: string) => void
     setAppInfo: (appInfo: AppInfo) => void

     traffective: any
   }
  }

type MainContentProps = {
  children: ReactNode
}

function MainContent({ children }: MainContentProps) {
  const { appVersion, setAppVersion, setPushSubscriptionId, user, setUser, debugLog, setAppInfo } = React.useContext(AppContext);
  const [queryInAppPurchases] = useQueryInAppPurchases()
  
  const router = useRouter();
  const path = router.asPath?.split("#")[0].split("?")[0]
  
  const [routerPush, routerReplace, routerReplaceSync] = useLoggedRouter();
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigationUrl, setNavigationUrl] = useState(path);
  const [lastCompletedUrl, setLastCompletedUrl] = useState(path);
  const [lastCompletedBaseUrl, setLastCompletedBaseUrl] = useState(path);
  const [navigationType, setNavigationType] = useState(NavigationType.Internal)

  const [fireReady, setFireReady] = useState(false);

  const [isCmpOpen, setIsCmpOpen] = useState(false);

  let navigationTypeOverride: NavigationType | undefined = undefined

  const { headerDisabled } = React.useContext(AppContext);

  function getAppCategoryInformationInternal(url) {
    const catInformation = getCategoryInformation(url)
    return { url: url, backEnabled: catInformation.back, type: navigationTypeOverride || navigationType, isApp: headerDisabled }
  }

  useEffect(() => {
    window.logError = (message: string) => {
      logBugsnagError(new Error(message), (event) => {
        event.addMetadata('SmartphoneApp', {
          appVersion: appVersion,
          logger: 'MainContent'
        })
      })
    }
  }, [appVersion])

  useEffect(() => {
    if(window && window.queryPushSubscriptionId) {
      window.queryPushSubscriptionId();
    }
  }, [])

  const updateLoggedInUser = async () => {
    debugLog("* updateLoggedInUser " + JSON.stringify(user))
    if(user && user.tokenId) {
      const data = await update(user.tokenId);
      debugLog("* data " + JSON.stringify(data))
      if (data.user) {
        setUser({...user, 
          userName: data.user.userName, 
          userSurname: data.user.userSurname, 
          userEmail: data.user.userEmail});
      }
    }
  }

  useEffect(() => {
    window.setAppVersion = (version: string) => {
      setAppVersion(version)
    }
    window.setPushSubscriptionId = (id: string) => {
      setPushSubscriptionId(id)
    }
    
    window.pushesGetChannelId = () => {
      return process.env.NEXT_PUBLIC_APP_PUSH_CHANNEL_ID ?? ''
    }

    window.setAppInfo = (appInfo: any) => {
      appInfo.platform = parsePlatform(appInfo.platform);
      appInfo.iapAvailable = appInfo.iapAvailable === true || appInfo.iapAvailable === "true"
      setAppInfo(appInfo)
    }

    const toExternalUrl = (url: String) => {
      const externalBase = process.env.NEXT_PUBLIC_VNP_EXTERNAL_BASE_URL ?? ''
      const baseUrl = process.env.NEXT_PUBLIC_VNP_BASE_URL
      const nakedUrl = process.env.NEXT_PUBLIC_VNP_BASE_NAKED_URL
      let out = url as string
      // guard against replace('', x) which would prepend x to the whole url
      if (baseUrl) {
        out = out.replace(baseUrl, externalBase)
      }
      if (nakedUrl) {
        out = out.replace(nakedUrl, externalBase)
      }
      return out
    }

    const canNavigateTo = (url: String) => {
      const initialUrl = url;
      if(url.endsWith("/bg")){
        return false;
      }

      if (!url.startsWith("/cmp") && !url.endsWith("/cmp")) {
        const initial_url = url
        let vnpBaseMatch = false
        //also catch NEXT_PUBLIC_VNP_BASE_URL without "www."
        // guard against startsWith('')/replace('', x) which would match/prepend on every url
        const vnpBaseUrl = process.env.NEXT_PUBLIC_VNP_BASE_URL
        const vnpNakedUrl = process.env.NEXT_PUBLIC_VNP_BASE_NAKED_URL
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL ?? ''
        if((vnpBaseUrl && url.startsWith(vnpBaseUrl)) || (vnpNakedUrl && url.startsWith(vnpNakedUrl))) {
          vnpBaseMatch = true
          if (vnpBaseUrl) {
            url = url.replace(vnpBaseUrl, backendUrl)
          }
          if (vnpNakedUrl) {
            url = url.replace(vnpNakedUrl, backendUrl)
          }
        }

        

        //https://www.nn.de/
        const isHome = initialUrl == process.env.NEXT_PUBLIC_VNP_BASE_URL || initialUrl == process.env.NEXT_PUBLIC_VNP_BASE_NAKED_URL || initialUrl == process.env.NEXT_PUBLIC_VNP_BASE_URL + "/" || initialUrl == process.env.NEXT_PUBLIC_VNP_BASE_NAKED_URL + "/";
        if(isHome) {
          return true
        }

        const regex = /http.*[-\/](\d[.]\d*)/i;
        const found = url.match(regex)
        
        let id: string | undefined = undefined
        if(found?.length == 2) {
          id = found[1]
        }
        else if(url.includes("/1.1478739")) {
          //fallback for wrong qr code
          id = "1.14778739"
        }
        if(id == "1.1478739") {
          //fallback for wrong qr code
          id = "1.14778739"
        }
        

        const is_newsletter_url = (initial_url?.split('?')[0] == `${process.env.NEXT_PUBLIC_VNP_BASE_URL}/${id}`) || (vnpBaseMatch && id && initial_url.includes("?email="))

        if(is_newsletter_url) {
          return false
        }
        else if(id) {
          return true
        }
        else if(vnpBaseMatch) {
          //no id found just a ressort, navigate home
          return false
        }
        else {
          return true
        }
      }
      else {
        return true
      }
    }

    const fixUrlsAndNavigateExternal = (url: String) => {
      if(url == "http://www.nn.de/newsletter?bg") {
        if(window.externalNavigate) {
          window.externalNavigate({url: url})
          return true
        }
      }
      return false
    }

    const intNavigate = async (url: String, navigationTypeOverride: NavigationType) => {
      const initialUrl = url;
      if (!url.startsWith("/cmp") && !url.endsWith("/cmp")) {
        let vnpBaseMatch = false
        // guard against startsWith('')/replace('', x) which would match/prepend on every url
        const vnpBaseUrl = process.env.NEXT_PUBLIC_VNP_BASE_URL
        const vnpNakedUrl = process.env.NEXT_PUBLIC_VNP_BASE_NAKED_URL
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL ?? ''
        if((vnpBaseUrl && url.startsWith(vnpBaseUrl)) || (vnpNakedUrl && url.startsWith(vnpNakedUrl))) {
          vnpBaseMatch = true
          if (vnpBaseUrl) {
            url = url.replace(vnpBaseUrl, backendUrl)
          }
          if (vnpNakedUrl) {
            url = url.replace(vnpNakedUrl, backendUrl)
          }
        }

        closePianoOffer()
        if(isCmpOpen) {
          if (window && (window as any).__cmp) {
            (window as any).__cmp('close')
          }
          setIsCmpOpen(false)
        }
        setNavigationType(navigationTypeOverride)

        //https://www.nn.de/
        const isHome = initialUrl == process.env.NEXT_PUBLIC_VNP_BASE_URL || initialUrl == process.env.NEXT_PUBLIC_VNP_BASE_NAKED_URL || initialUrl == process.env.NEXT_PUBLIC_VNP_BASE_URL + "/" || initialUrl == process.env.NEXT_PUBLIC_VNP_BASE_NAKED_URL + "/";
        if(isHome) {
          await routerPush('/')
          return
        }

        const regex = /http.*[-\/](\d[.]\d*)/i;
        const found = url.match(regex)
        
        let id: string | undefined = undefined
        if(found?.length == 2) {
          id = found[1]
        }
        else if(url.includes("/1.1478739")) {
          //fallback for wrong qr code
          id = "1.14778739"
        }
        if(id == "1.1478739") {
          //fallback for wrong qr code
          id = "1.14778739"
        }

        if(id) {
          if(id.startsWith("1") && !url.includes(`/ressort/${id}`) && !url.includes('/thema/')) {
            await routerPush(`/article/${id}`)
          }
          else {
            await routerPush(`/ressort/${id}`)
          }
        }
        else if(vnpBaseMatch) {
          //no id found just a ressort, navigate home
          await routerPush('/')
        }
        else {
          await routerPush(`${url}`)
        }
      }
      else {
        if (window && (window as any).__cmp) {
          (window as any).__cmp('showScreen')
          setIsCmpOpen(true)
        }
      }
    }

    window.navigateTo = (url: String) => {
      debugLog(`navigateTo: ${url} -> ${canNavigateTo(url)}`)
      if(fixUrlsAndNavigateExternal(url)) {
        return
      }
      if(canNavigateTo(url)) {
        intNavigate(url, NavigationType.ExternalNavigateTo)
        updateLoggedInUser();
      }
      else if(window.externalNavigate) {
        window.externalNavigate({url: toExternalUrl(url)})
      }
      else {
        //fallback for old apps
        intNavigate(url, NavigationType.ExternalNavigateTo)
      }
    }

    window.navigateToWithoutNotification = (url: String) => {
      debugLog("navigateToWithoutNotification")
      if(canNavigateTo(url)) {
        intNavigate(url, NavigationType.ExternalNavigateToWithoutNotification)
        updateLoggedInUser();
      }
      else if(window.externalNavigate) {
        window.externalNavigate({url: toExternalUrl(url)})
      }
    }

    window.navigateBack = () => {
      closePianoOffer()
      navigationTypeOverride = NavigationType.ExternalBack
      setNavigationType(navigationTypeOverride)
      // console.log(`** navigateBack`, router.asPath)
      router.back();
    }

    window.reload = async (inBackgroundForSeconds?: number) => {
      if(inBackgroundForSeconds && inBackgroundForSeconds > 0 && inBackgroundForSeconds < 120 * 60) {
        debugLog("reload: " + inBackgroundForSeconds + " > 120 * 60, skipping")
        return "skipped";
      }
      debugLog("reload")

      updateLoggedInUser()
      if (lastCompletedUrl) {
        let url = lastCompletedUrl
        if (!lastCompletedUrl.includes("iosapp=1")) {
          if (lastCompletedUrl.includes("?")) {
            url = lastCompletedUrl + "&iosapp=1"
          }
          else {
            url = lastCompletedUrl + "?iosapp=1"
          }
        }
        //fix strange errors like this /article/[id]?iosapp=1 or /ressort/[id]?iosapp=1
        const sanitizedUrl = sanitizeUrl(url, appVersion)
        navigationTypeOverride = NavigationType.ExternalReload
        setNavigationType(navigationTypeOverride)
        showLoading(sanitizedUrl, { shallow: false })
        await routerReplace(sanitizedUrl)
        hideLoading(sanitizedUrl, { shallow: false })
        queryInAppPurchases()

        setFireReady(true)
      }
      return "reloaded";
    }

    window.reloadSync = (inBackgroundForSeconds?: number) => {
      if(inBackgroundForSeconds && inBackgroundForSeconds > 0 && inBackgroundForSeconds < 120 * 60) {
        debugLog("reload: " + inBackgroundForSeconds + " > 120 * 60, skipping")
        return "skipped";
      }
      debugLog("reload")

      updateLoggedInUser()
      if (lastCompletedUrl) {
        let url = lastCompletedUrl
        if (!lastCompletedUrl.includes("iosapp=1")) {
          if (lastCompletedUrl.includes("?")) {
            url = lastCompletedUrl + "&iosapp=1"
          }
          else {
            url = lastCompletedUrl + "?iosapp=1"
          }
        }
        //fix strange errors like this /article/[id]?iosapp=1 or /ressort/[id]?iosapp=1
        const sanitizedUrl = sanitizeUrl(url, appVersion)
        navigationTypeOverride = NavigationType.ExternalReload
        setNavigationType(navigationTypeOverride)
        showLoading(sanitizedUrl, { shallow: false })
        routerReplaceSync(sanitizedUrl)
        hideLoading(sanitizedUrl, { shallow: false })
        queryInAppPurchases()

        setFireReady(true)
      }
      return "reloaded";
    }

    window.getNavigationData = () => {
      return getAppCategoryInformationInternal(navigationUrl)
    }

    // function routeChangeStart(url, { shallow }) {
    //   //intercept call
    //   // if (window.confirm('Are you sure you want to leave?'))
    //   //   leaveConfirmed = true;
    //   // else {
    //   //   router.events.emit('routeChangeError');
    //   //   throw 'routeChange aborted.';
    //   // }
    //   if(!shallow) {
    //     showLoading(url)
    //   }
    // }

    function isValidForExternalNotification(url, shallow) {
      if(shallow == true || navigationType == NavigationType.ExternalNavigateToWithoutNotification) {
        // console.log(`** isValidForExternalNotification: shallow, url: ${url} -> false 1`)
        return false
      }
      if (lastCompletedBaseUrl === "/" && url === "/") {
        // console.log(`** isValidForExternalNotification: lastCompletedBaseUrl === "/" && url === "/") -> false 2`)
        return false
      }
      if(lastCompletedBaseUrl?.length > 0 && url?.length > 0) {
        // console.log(`** isValidForExternalNotification: lastCompletedBaseUrl?.length > 0 && url?.length > 0 , lastCompletedBaseUrl: ${lastCompletedBaseUrl}, url: ${url} -> ${!url.includes(lastCompletedBaseUrl) || (lastCompletedBaseUrl.length <= 1 && !url.includes("/#"))} 3`)
        return !url.includes(lastCompletedBaseUrl) || (lastCompletedBaseUrl.length <= 1 && !url.includes("/#"))
      }
      // console.log(`** isValidForExternalNotification: true 4`)
      return false
    }

    function showLoading(url, { shallow }) {
      debugLog(`** showLoading: url: ${url}, shallow: ${shallow}`)
      // console.log(`** showLoading: url: ${url}`)
      setIsNavigating(true);
      setNavigationUrl(url)
      if (window.navigationStart && isValidForExternalNotification(url, shallow)) {
        const catInfos = getAppCategoryInformationInternal(url)
        window.navigationStart(catInfos)
      }

      // if(adsReady && window.traffective && window.traffective.routeChangeStart) {
      //   window.traffective.routeChangeStart()
      // }
    }

    function hideLoading(url, { shallow }) {
      debugLog(`** hideLoading: url: ${url}, shallow: ${shallow}`)
      // console.log(`** hideLoading: url: ${url}`)
      if(url.startsWith("/user") || url.startsWith("/navigation")) {
        setIsNavigating(false);
      }
      else if (window && window.history && window.history.state && window.history.state.options && window.history.state.options.shallow == true) {
        debugLog(`** hideLoading: shallow, url: ${url}`)
        setIsNavigating(false);
      }
      else {
        setIsNavigating(false);
      }

      if (window.navigationComplete && isValidForExternalNotification(url, shallow)) {
        const catInfos = getAppCategoryInformationInternal(url)
        window.navigationComplete(catInfos)
      }
      
      setLastCompletedBaseUrl(url.split('?')[0].split('#')[0])
      setLastCompletedUrl(url)
      setNavigationUrl(url)
      setNavigationType(NavigationType.Internal)

      // if(adsReady && window.traffective && window.traffective.routeChangeComplete) {
      //   window.traffective.routeChangeComplete()
      // }
    }

    Router.events.on("routeChangeStart", showLoading);
    Router.events.on("routeChangeComplete", hideLoading);
    Router.events.on("routeChangeError", (err, url, { shallow }) => {
      if (err.cancelled) {
        // console.log(`** routeChangeError cancelled: ${err}, ${url} ${shallow}`)
        return;
      }
      // console.log(`** routeChangeError: ${err}, ${url} ${shallow}`)
      hideLoading(url, { shallow });
    });

    return function () {
      Router.events.off("routeChangeStart", showLoading);
      Router.events.off("routeChangeComplete", hideLoading);
      Router.events.off("routeChangeError", (err, url, { shallow }) => {
        if (err.cancelled) {
          // console.log(`** routeChangeError cancelled: ${err}, ${url} ${shallow}`)
          return;
        }
        // console.log(`** routeChangeError: ${err}, ${url} ${shallow}`)
        hideLoading(url, { shallow });
      });
    }
  }, [lastCompletedBaseUrl, lastCompletedUrl, navigationType, navigationUrl]);

  React.useEffect(() => {
    setTimeout(() => {
      if(window.ready) {
        window.ready()
      }
    }, 1000);
  }, []);

  React.useEffect(() => {
    if(fireReady) {
      setTimeout(() => {
        if(window.ready) {
          window.ready()
        }
      }, 1000);
      setFireReady(false)
    }
  }, [fireReady]);

  let [showDarkTheme, setShowDarkTheme] = React.useState(false);
  let [showDarkThemeLocalStorage, setShowDarkThemeLocalStorage] = useLocalStorage('dark-theme', false);

  React.useEffect(() => {
    if (process.browser) {
      setShowDarkTheme(showDarkThemeLocalStorage);
    }
  }, [showDarkThemeLocalStorage]);

  const [headerHeight] = useHeaderHeight()

  return (
    <React.Fragment>
      {headerDisabled === false && <Header />}
      <main aria-label="Inhaltsbereich" className={`content-area ${headerDisabled ? "content-area--app" : ""}`} style={{paddingTop: headerHeight + 'px', '--header-height': headerHeight + 'px' } as React.CSSProperties}>
        {isNavigating ? (<div className="content-loading text-center"><Loading /></div>) : children}
        <AudioPlayer  />
      </main>
      {headerDisabled === false && <Footer />}
    </React.Fragment>
  );
}

export default MainContent;
